// Security review: serialize logout-all with wallet auth and invalidate owner views immediately.
import { useState } from 'react';

import { Api } from '../lib/api.js';
import { useApi, useWallet, WalletState } from '../lib/wallet.jsx';
import { Badge, Button, Card, ErrorState, Hash, Modal, Skeleton } from '../components/ui.jsx';

/**
 * Wallet, network, token and privacy settings.
 *
 * The $COR surface is here rather than as its own destination because pre-TGE
 * it has nothing to do. §14.1 forbids rendering fake balances or enabling
 * deposit controls, so what it shows is an explanation, not a dashboard.
 */
export function Settings({ pushToast }) {
  const wallet = useWallet();
  const cor = useApi(() => Api.corStatus(), [wallet.sessionReady], { skip: !wallet.sessionReady });
  const sessions = useApi(() => Api.sessions(), [wallet.sessionReady], { skip: !wallet.sessionReady });
  const [confirmErase, setConfirmErase] = useState(false);
  const [busy, setBusy] = useState(null);

  return (
    <div className="stack">
      <header className="page-header">
        <h1 className="page-title">Settings</h1>
      </header>

      <Card title="Wallet and network">
        <dl className="facts">
          <Row label="Connected wallet" value={wallet.address ? <Hash value={wallet.address} label="wallet" /> : <span className="text-muted">Not connected</span>} />
          <Row label="Session" value={wallet.isAuthenticated ? <Badge tone="success">Wallet connected</Badge> : <Badge tone="neutral">Guest (off chain)</Badge>} />
          <Row label="Expires" value={wallet.session?.expiresAt ? new Date(wallet.session.expiresAt).toLocaleString() : '—'} />
          <Row label="Network" value={wallet.chainId ? `chain ${wallet.chainId}` : '—'} />
          <Row label="Expected network" value={`chain ${wallet.expectedChainId} (opBNB)`} />
        </dl>

        {wallet.wrongNetwork && (
          <div className="notice notice--warning">
            <p className="notice__title">Wrong network</p>
            <p className="notice__body">Cortis operates on opBNB. Transactions will not work until you switch.</p>
            <Button variant="primary" loading={wallet.state === WalletState.NETWORK_SWITCHING} onClick={wallet.switchNetwork}>
              Switch network
            </Button>
          </div>
        )}

        {!wallet.isAuthenticated && (
          <p className="text-muted">You are working off chain as a guest. Everything here is saved to your account. Connect a wallet to add the on-chain layer: check-in, map, workflow and deploy actions, plus signed workflow authorisations. Your current work carries over.</p>
        )}

        {!wallet.isAuthenticated && (
          <div className="row-actions">
            {!wallet.provider ? (
              <span className="text-muted">No EVM wallet detected. Install one, then reload this page.</span>
            ) : (
              <Button variant="primary" onClick={wallet.promptConnect}>
                {wallet.state === WalletState.CONNECTED ? 'Sign in' : wallet.wrongNetwork ? 'Switch network' : 'Connect wallet'}
              </Button>
            )}
          </div>
        )}

        <div className="row-actions">
          {wallet.isAuthenticated && <Button variant="ghost" onClick={wallet.signOut}>Sign out</Button>}
          {wallet.isAuthenticated && <Button
            variant="ghost"
            loading={busy === 'logout-all'}
            onClick={async () => {
              setBusy('logout-all');
              try {
                const r = await wallet.signOutEverywhere();
                pushToast({ tone: 'success', message: `${r.revoked} session(s) revoked.` });
              } catch (err) {
                pushToast({ tone: 'danger', message: err.message });
              } finally {
                setBusy(null);
              }
            }}
          >
            Sign out everywhere
          </Button>}
        </div>
      </Card>

      <Card title="$COR and vault">
        {cor.loading && <Skeleton rows={2} />}
        {cor.error && <ErrorState error={cor.error} onRetry={cor.reload} />}
        {cor.data && !cor.data.enabled && (
          <div className="notice">
            <p className="notice__title">Pre-TGE · $COR is not live</p>
            <p className="notice__body">
              Cortis currently operates gas-only on opBNB. No token is required for anything: checking in, mapping,
              generating a workflow and deploying an agent all work without $COR.
            </p>
            <p className="notice__body">
              Points are non-transferable, non-withdrawable product counters. They are not a promise of $COR, an
              airdrop, or financial value, and there is no fixed conversion.
            </p>
            {/* Deliberately no balance rows and no deposit, lock or withdraw
                controls. Rendering a zero balance would imply a live
                integration that does not exist. */}
            <dl className="facts">
              <Row label="Phase" value={<span className="mono">{cor.data.phase}</span>} />
              <Row label="Reason" value={<span className="mono">{cor.data.reason}</span>} />
            </dl>
          </div>
        )}
        {cor.data?.enabled && (
          <p className="text-muted">
            The token surface is enabled for this environment. Balances are read per chain and are never merged into
            one number.
          </p>
        )}
      </Card>

      <Card title="Sessions">
        {sessions.loading && <Skeleton rows={3} />}
        {sessions.data && (
          <table className="table">
            <thead><tr><th>Created</th><th>Expires</th><th>State</th></tr></thead>
            <tbody>
              {sessions.data.sessions.map((s) => (
                <tr key={s.id}>
                  <td className="mono">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="mono">{new Date(s.expires_at).toLocaleString()}</td>
                  <td><Badge tone={s.revoked_at ? 'neutral' : 'success'}>{s.revoked_at ? 'revoked' : 'active'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Your data">
        <p>
          Prompts, agent responses, memory, tool inputs and outputs, and workflow instructions are stored encrypted
          and never placed on chain. What reaches the chain is hashes, counters and transaction metadata.
        </p>
        <p className="text-muted">
          Erasure destroys the key that protects your private data, which makes every stored copy unreadable at once.
          On-chain hashes cannot be deleted, but a hash does not reveal the content it committed to.
        </p>
        <Button variant="danger" onClick={() => setConfirmErase(true)}>Erase my private data</Button>
      </Card>

      <Modal
        open={confirmErase}
        title="Erase your private data"
        onClose={() => setConfirmErase(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmErase(false)}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy === 'erase'}
              onClick={async () => {
                setBusy('erase');
                try {
                  const r = await Api.erase();
                  pushToast({ tone: 'success', message: `Erased ${r.erased.messages} messages and ${r.erased.memories} memories.` });
                  setConfirmErase(false);
                } catch (err) {
                  pushToast({ tone: 'danger', message: err.message });
                } finally {
                  setBusy(null);
                }
              }}
            >
              Erase permanently
            </Button>
          </>
        }
      >
        <p><strong>This cannot be undone.</strong></p>
        <p>Every message, memory and stored payload for your account becomes permanently unreadable.</p>
        <p className="text-muted">
          Your agents, their passports and their on-chain proofs remain. Those records contain only hashes and
          counters, and the hashes will commit to content that no longer exists anywhere.
        </p>
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="facts__row">
      <dt className="facts__label">{label}</dt>
      <dd className="facts__value">{value}</dd>
    </div>
  );
}
